import dayjs from 'dayjs';
import {
  DASHBOARD_FEEDBACK_CATEGORIES,
  DASHBOARD_FEEDBACK_STATUS
} from './dashboardContract';

export const DASHBOARD_FEEDBACK_PAGE_SIZE = 10;
export const DASHBOARD_FEEDBACK_TITLE_MAX_LENGTH = 150;
export const DASHBOARD_FEEDBACK_CONTACT_NAME_MAX_LENGTH = 100;
export const DASHBOARD_FEEDBACK_CONTACT_EMAIL_MAX_LENGTH = 254;
export const DASHBOARD_FEEDBACK_CONTACT_PHONE_MAX_LENGTH = 50;
export const DASHBOARD_FEEDBACK_RESOLUTION_MAX_LENGTH = 1000;
export const DASHBOARD_FEEDBACK_DELETE_REASON_MAX_LENGTH = 500;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CATEGORY_LABELS = Object.freeze({
  service: 'Service',
  staff: 'Staff',
  facilities: 'Facilities',
  safety: 'Safety',
  compliance: 'Compliance',
  compliment: 'Compliment',
  complaint: 'Complaint',
  other: 'Other'
});

const STATUS_LABELS = Object.freeze({
  [DASHBOARD_FEEDBACK_STATUS.OPEN]: 'Open',
  [DASHBOARD_FEEDBACK_STATUS.IN_PROGRESS]: 'In progress',
  [DASHBOARD_FEEDBACK_STATUS.RESOLVED]: 'Resolved',
  [DASHBOARD_FEEDBACK_STATUS.NO_ACTION]: 'No action',
  [DASHBOARD_FEEDBACK_STATUS.REOPENED]: 'Reopened',
  [DASHBOARD_FEEDBACK_STATUS.DELETED]: 'Deleted'
});

const TRANSITIONS = Object.freeze({
  [DASHBOARD_FEEDBACK_STATUS.OPEN]: [
    DASHBOARD_FEEDBACK_STATUS.IN_PROGRESS,
    DASHBOARD_FEEDBACK_STATUS.RESOLVED,
    DASHBOARD_FEEDBACK_STATUS.NO_ACTION
  ],
  [DASHBOARD_FEEDBACK_STATUS.IN_PROGRESS]: [
    DASHBOARD_FEEDBACK_STATUS.RESOLVED,
    DASHBOARD_FEEDBACK_STATUS.NO_ACTION
  ],
  [DASHBOARD_FEEDBACK_STATUS.RESOLVED]: [DASHBOARD_FEEDBACK_STATUS.REOPENED],
  [DASHBOARD_FEEDBACK_STATUS.NO_ACTION]: [DASHBOARD_FEEDBACK_STATUS.REOPENED],
  [DASHBOARD_FEEDBACK_STATUS.REOPENED]: [
    DASHBOARD_FEEDBACK_STATUS.IN_PROGRESS,
    DASHBOARD_FEEDBACK_STATUS.RESOLVED,
    DASHBOARD_FEEDBACK_STATUS.NO_ACTION
  ],
  [DASHBOARD_FEEDBACK_STATUS.DELETED]: []
});

const STATUS_COLOURS = Object.freeze({
  [DASHBOARD_FEEDBACK_STATUS.OPEN]: 'info',
  [DASHBOARD_FEEDBACK_STATUS.IN_PROGRESS]: 'warning',
  [DASHBOARD_FEEDBACK_STATUS.RESOLVED]: 'success',
  [DASHBOARD_FEEDBACK_STATUS.NO_ACTION]: 'default',
  [DASHBOARD_FEEDBACK_STATUS.REOPENED]: 'warning',
  [DASHBOARD_FEEDBACK_STATUS.DELETED]: 'default'
});

const nullableText = (value) => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || null;
};

const isCalendarDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
};

export const DASHBOARD_FEEDBACK_CATEGORY_OPTIONS =
  DASHBOARD_FEEDBACK_CATEGORIES.map((value) => ({
    label: CATEGORY_LABELS[value],
    value
  }));

export const createEmptyDashboardFeedbackForm = (
  receivedDate = dayjs().format('YYYY-MM-DD')
) => ({
  title: '',
  category: '',
  comment: '',
  receivedDate,
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  ownerUserId: null
});

export const createDashboardFeedbackForm = (feedback) => ({
  title: feedback?.title ?? '',
  category: feedback?.category ?? '',
  comment: feedback?.comment ?? '',
  receivedDate: feedback?.receivedDate ?? dayjs().format('YYYY-MM-DD'),
  contactName: feedback?.contact?.name ?? '',
  contactEmail: feedback?.contact?.email ?? '',
  contactPhone: feedback?.contact?.phone ?? '',
  ownerUserId: feedback?.ownerUserId ?? null
});

export const validateDashboardFeedbackForm = ({ approver, form }) => {
  const errors = {};
  const title = nullableText(form.title);
  const comment = nullableText(form.comment);
  const contactName = nullableText(form.contactName);
  const contactEmail = nullableText(form.contactEmail);
  const contactPhone = nullableText(form.contactPhone);

  if (title && title.length > DASHBOARD_FEEDBACK_TITLE_MAX_LENGTH) {
    errors.title =
      'Title cannot exceed ' +
      DASHBOARD_FEEDBACK_TITLE_MAX_LENGTH +
      ' characters.';
  }
  if (!comment) errors.comment = 'Feedback details are required.';
  if (!isCalendarDate(form.receivedDate)) {
    errors.receivedDate = 'Enter a valid received date.';
  }
  if (
    contactName &&
    contactName.length > DASHBOARD_FEEDBACK_CONTACT_NAME_MAX_LENGTH
  ) {
    errors.contactName =
      'Contact name cannot exceed ' +
      DASHBOARD_FEEDBACK_CONTACT_NAME_MAX_LENGTH +
      ' characters.';
  }
  if (
    contactEmail &&
    (contactEmail.length > DASHBOARD_FEEDBACK_CONTACT_EMAIL_MAX_LENGTH ||
      !EMAIL_PATTERN.test(contactEmail))
  ) {
    errors.contactEmail = 'Enter a valid contact email address.';
  }
  if (
    contactPhone &&
    contactPhone.length > DASHBOARD_FEEDBACK_CONTACT_PHONE_MAX_LENGTH
  ) {
    errors.contactPhone =
      'Contact phone cannot exceed ' +
      DASHBOARD_FEEDBACK_CONTACT_PHONE_MAX_LENGTH +
      ' characters.';
  }
  if (!approver?.user_id) errors.approver = 'Select an approver.';

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    payload: {
      title,
      category: form.category || null,
      comment,
      receivedDate: form.receivedDate,
      contact: {
        name: contactName,
        email: contactEmail,
        phone: contactPhone
      },
      ownerUserId: form.ownerUserId ?? null,
      approverUserId: approver?.user_id ?? null
    }
  };
};

export const validateDashboardFeedbackTransition = (
  feedbackStatus,
  resolutionNotes
) => {
  const notes = nullableText(resolutionNotes);
  const requiresNotes =
    feedbackStatus === DASHBOARD_FEEDBACK_STATUS.RESOLVED ||
    feedbackStatus === DASHBOARD_FEEDBACK_STATUS.NO_ACTION;
  let error = null;

  if (!feedbackStatus) error = 'Choose a status.';
  else if (requiresNotes && !notes) {
    error = 'Resolution notes are required for this status.';
  } else if (notes && notes.length > DASHBOARD_FEEDBACK_RESOLUTION_MAX_LENGTH) {
    error =
      'Resolution notes cannot exceed ' +
      DASHBOARD_FEEDBACK_RESOLUTION_MAX_LENGTH +
      ' characters.';
  }

  return {
    error,
    isValid: !error,
    payload: {
      feedbackStatus,
      resolutionNotes: requiresNotes ? notes : null
    }
  };
};

export const validateDashboardFeedbackDelete = (reason, approver) => {
  const normalizedReason = nullableText(reason);
  const errors = {};
  if (!normalizedReason) errors.reason = 'A removal reason is required.';
  else if (
    normalizedReason.length > DASHBOARD_FEEDBACK_DELETE_REASON_MAX_LENGTH
  ) {
    errors.reason =
      'Removal reason cannot exceed ' +
      DASHBOARD_FEEDBACK_DELETE_REASON_MAX_LENGTH +
      ' characters.';
  }
  if (!approver?.user_id) errors.approver = 'Select an approver.';

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    payload: {
      reason: normalizedReason,
      approverUserId: approver?.user_id ?? null
    }
  };
};

export const getDashboardFeedbackCategoryLabel = (category) =>
  CATEGORY_LABELS[category] ?? 'Uncategorized';

export const getDashboardFeedbackStatusLabel = (status) =>
  STATUS_LABELS[status] ?? status;

export const getDashboardFeedbackStatusColour = (status) =>
  STATUS_COLOURS[status] ?? 'default';

export const getDashboardFeedbackTransitions = (status) =>
  TRANSITIONS[status] ?? [];

export const formatDashboardFeedbackDate = (value) =>
  value && dayjs(value).isValid()
    ? dayjs(value).format('MMM D, YYYY')
    : 'Unknown';

export const formatDashboardFeedbackTimestamp = (value) =>
  value && dayjs(value).isValid()
    ? dayjs(value).format('MMM D, YYYY h:mm A')
    : 'Unknown';
